/**
 * SessionMemoAction — open the session's prioritization memo, or write it.
 *
 * The one place a person reaches the memo from a session. It replaced two
 * earlier explainers on 2026-10-14 (DEPRECATION_REGISTER DEP-032): the Results
 * tab's inline "Explain" panel and the Strategic Narrative panel on the results
 * pages. Publishing writes the memo by itself (sessionStore.publishSessionResults);
 * this control finds it, or writes one when a session has none yet.
 *
 *   variant="button" — one button in a toolbar
 *   variant="panel"  — a short block with the memo's headline
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { fetchLatestMemo, writePrioritizationMemo, type ReportArtifact } from '../../../lib/reportGenerationService';
import { isPrioritizationMemo, memoHref } from '../../../lib/prioritizationMemo';

export interface SessionMemoActionProps {
  sessionId: string;
  variant?: 'button' | 'panel';
}

export function SessionMemoAction({ sessionId, variant = 'button' }: SessionMemoActionProps) {
  const navigate = useNavigate();
  const [memo, setMemo] = useState<ReportArtifact | null>(null);
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchLatestMemo(sessionId)
      .then((m) => alive && setMemo(m))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [sessionId]);

  const write = async () => {
    setWriting(true);
    setError(null);
    const res = await writePrioritizationMemo(sessionId);
    setWriting(false);
    if (!res.ok || !res.artifact) {
      setError(res.error ?? 'The memo could not be written.');
      return;
    }
    setMemo(res.artifact);
    navigate(memoHref(sessionId, res.artifact.id));
  };

  const btn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-60';

  if (variant === 'button') {
    return (
      <span className="inline-flex flex-col">
        {memo ? (
          <Link to={memoHref(sessionId, memo.id)} className={btn} data-testid="open-memo">
            <FileText className="h-3.5 w-3.5" />
            Memo
          </Link>
        ) : (
          <button type="button" onClick={() => void write()} disabled={loading || writing} className={btn} data-testid="write-memo">
            {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {writing ? 'Writing the memo…' : 'Write the memo'}
          </button>
        )}
        {error && <span className="mt-1 text-xs text-[var(--coral)] max-w-xs">{error}</span>}
      </span>
    );
  }

  const headline = memo && isPrioritizationMemo(memo.content) ? memo.content.headline : null;
  return (
    <section className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="session-memo-panel">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-[var(--text-ter)]">Prioritization memo</p>
      {loading ? (
        <p className="mt-2 text-sm text-[var(--text-ter)]">Looking for the memo…</p>
      ) : memo ? (
        <>
          {headline && <p className="mt-2 text-base font-medium text-[var(--text-pri)]">{headline}</p>}
          <p className="mt-1 text-sm text-[var(--text-sec)]">
            Why these ranked where they did, what was traded off, and what just missed the line — for people who were not in the room.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link to={memoHref(sessionId, memo.id)} className={btn}>
              <FileText className="h-3.5 w-3.5" />
              Read the memo
            </Link>
            <button type="button" onClick={() => void write()} disabled={writing} className={btn}>
              {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {writing ? 'Writing…' : 'Write it again'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-[var(--text-sec)]">
            No memo yet. It is written when the results are published, or you can write it now.
          </p>
          <button type="button" onClick={() => void write()} disabled={writing} className={`${btn} mt-3`}>
            {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {writing ? 'Writing the memo…' : 'Write the memo'}
          </button>
        </>
      )}
      {error && <p className="mt-2 text-xs text-[var(--coral)]">{error}</p>}
    </section>
  );
}

export default SessionMemoAction;
